<template>
  <!-- Projects Dashboard -->
  <v-col>
    <!-- Compact Header Section with Inline Tabs -->
    <div class="projects-view-header-compact mb-3">
      <v-toolbar
        flat
        color="white"
        class="header-toolbar px-0 py-0"
        height="56"
      >
        <!-- Title Icon -->
        <div class="header-icon-wrapper mr-3">
          <font-awesome-icon
            :icon="['fas', 'folder-open']"
            size="lg"
            class="primary--text"
          />
        </div>

        <!-- Inline Tabs -->
        <v-tabs
          v-model="activeTab"
          color="primary"
          slider-color="primary"
          show-arrows
          class="projects-view-tabs-inline flex-grow-1"
          height="56"
        >
          <v-tab
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-compact"
          >
            <font-awesome-icon
              :icon="tab.icon"
              class="mr-2"
              size="sm"
            />
            {{ tab.label }}
            <v-chip
              v-if="tab.count !== undefined"
              x-small
              outlined
              class="ml-2 tab-count-chip"
            >
              {{ tab.count }}
            </v-chip>
          </v-tab>
        </v-tabs>

        <v-spacer />

        <!-- Action Buttons -->
        <div class="header-actions d-flex align-center">
          <!-- Add Project Button (Admin Only) -->
          <v-btn
            v-if="isUserInternalAdmin"
            color="primary"
            depressed
            class="add-project-btn-compact"
            @click="showFormProject"
          >
            <font-awesome-icon
              :icon="['fas', 'plus']"
              size="sm"
            />
            <span class="ml-2 d-none d-sm-inline">Add Project</span>
          </v-btn>
        </div>
      </v-toolbar>

      <v-divider />

      <!-- Compact Controls Bar -->
      <div class="controls-bar">
        <v-row class="align-center ma-0 pa-3 pl-2">
          <!-- Search Field -->
          <v-col
            cols="12"
            sm="6"
            md="5"
            lg="4"
            class="py-1"
          >
            <v-text-field
              v-model="searchTerm"
              placeholder="Search by name, code, ID, status..."
              single-line
              hide-details
              dense
              outlined
              clearable
              class="search-field-compact"
            >
              <template v-slot:prepend-inner>
                <font-awesome-icon
                  :icon="['fas', 'magnifying-glass']"
                  size="sm"
                  class="search-icon mt-1"
                />
              </template>
            </v-text-field>
          </v-col>

          <!-- Filter Button -->
          <v-col
            cols="auto"
            class="py-1 d-flex align-center"
          >
            <v-btn
              text
              outlined
              color="primary"
              :class="{ 'filter-active': hasActiveFilters }"
              class="filter-btn-compact"
              @click="showFilters = !showFilters"
            >
              <font-awesome-icon
                :icon="['fas', 'filter']"
                size="sm"
                class="mr-1"
              />
              <span>Filters</span>
              <v-chip
                v-if="hasActiveFilters"
                x-small
                color="primary"
                class="ml-2"
              >
                {{ activeFilterCount }}
              </v-chip>
            </v-btn>
          </v-col>

          <!-- Group By -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <v-select
              v-model="groupBy"
              :items="groupByOptions"
              label="Group by"
              clearable
              dense
              outlined
              hide-details
              style="min-width: 130px;"
              class="group-select-compact"
            />
          </v-col>

          <!-- View Mode Toggle -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <v-btn-toggle
              v-model="viewMode"
              mandatory
              dense
              borderless
              color="primary"
            >
              <v-btn
                value="cards"
                small
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <font-awesome-icon
                      :icon="['fas', 'grip']"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <span>Card View</span>
                </v-tooltip>
              </v-btn>
              <v-btn
                value="table"
                small
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <font-awesome-icon
                      :icon="['fas', 'table']"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <span>Table View</span>
                </v-tooltip>
              </v-btn>
            </v-btn-toggle>
          </v-col>

          <v-spacer class="d-none d-sm-flex" />
        </v-row>
      </div>
    </div>

    <!-- Projects -->
    <v-row v-if="haveProjects">
      <v-col>
        <!-- Filter Panel -->
        <v-expand-transition>
          <v-row v-if="showFilters">
            <v-col>
              <v-card
                flat
                class="pa-4 mb-3"
              >
                <v-row align="center">
                  <v-col class="col-auto">
                    <div class="d-flex align-center">
                      <font-awesome-icon
                        :icon="['fas', 'filter']"
                        class="primary--text mr-2"
                      />
                      <span class="subtitle-2 font-weight-medium">Filter Projects</span>
                    </div>
                  </v-col>

                  <v-spacer />

                  <v-col
                    v-if="hasActiveFilters"
                    class="col-auto"
                  >
                    <v-chip
                      small
                      color="primary"
                      text-color="white"
                      class="mr-2"
                    >
                      {{ activeFilterCount }} active
                    </v-chip>
                    <v-btn
                      small
                      text
                      color="primary"
                      @click="clearAllFilters"
                    >
                      Clear All
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row class="mt-2">
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <v-select
                      v-model="filters.billingCode"
                      :items="billingCodeOptions"
                      :label="filters.billingCode ? 'Billing Code' : 'Billing Code'"
                      :color="filters.billingCode ? 'primary' : undefined"
                      clearable
                      dense
                      outlined
                      hide-details
                      :class="{ 'primary--text': filters.billingCode }"
                    >
                      <template v-slot:label>
                        <span :class="{ 'primary--text font-weight-bold': filters.billingCode }">
                          Billing Code
                        </span>
                      </template>
                      <template v-slot:prepend-inner>
                        <font-awesome-icon
                          :icon="['fas', 'code']"
                          :style="{ color: filters.billingCode ? 'var(--v-primary-base)' : '#bdbdbd', marginTop: '2px' }"
                          size="sm"
                        />
                      </template>
                    </v-select>
                  </v-col>

                  <v-col
                    cols="12"
                    md="4"
                  >
                    <v-select
                      v-model="filters.projectStatus"
                      :items="projectStatusOptions"
                      :color="filters.projectStatus ? 'primary' : undefined"
                      clearable
                      dense
                      outlined
                      hide-details
                    >
                      <template v-slot:label>
                        <span :class="{ 'primary--text font-weight-bold': filters.projectStatus }">
                          Status
                        </span>
                      </template>
                      <template v-slot:prepend-inner>
                        <font-awesome-icon
                          :icon="['fas', 'clipboard-list']"
                          :style="{ color: filters.projectStatus ? 'var(--v-primary-base)' : '#bdbdbd', marginTop: '2px' }"
                          size="sm"
                        />
                      </template>
                    </v-select>
                  </v-col>

                  <v-col
                    cols="12"
                    md="4"
                  >
                    <v-select
                      v-model="filters.projectType"
                      :items="projectTypeOptions"
                      :color="filters.projectType ? 'primary' : undefined"
                      clearable
                      dense
                      outlined
                      hide-details
                    >
                      <template v-slot:label>
                        <span :class="{ 'primary--text font-weight-bold': filters.projectType }">
                          Project Type
                        </span>
                      </template>
                      <template v-slot:prepend-inner>
                        <font-awesome-icon
                          :icon="['fas', 'layer-group']"
                          :style="{ color: filters.projectType ? 'var(--v-primary-base)' : '#bdbdbd', marginTop: '2px' }"
                          size="sm"
                        />
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </v-expand-transition>

        <!-- Card View -->
        <v-row v-if="viewMode === 'cards'">
          <v-col>
            <!-- Regular View: All Projects -->
            <div v-if="!isGroupingEnabled">
              <!-- List of All Projects -->
              <v-row>
                <v-col>
                  <!-- Toggle Button and Total Projects -->
                  <v-row>
                    <v-col>
                      <v-row>
                        <v-col>
                          <!-- Toggle Group -->
                          <font-awesome-icon
                            :icon="['fas', showAllProjects ? 'chevron-down' : 'chevron-right']"
                            size="xs"
                            class="gray--text cursor-pointer"
                            @click.stop="toggleAllProjects"
                          />

                          <span
                            class="pl-1 subtitle-1 font-weight-medium cursor-pointer"
                            @click.stop="toggleAllProjects"
                          >All</span>

                          <!-- Total Projects -->
                          <v-chip
                            class="ml-2"
                            x-small
                          >
                            {{ allProjects.length }}
                          </v-chip>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>

                  <!-- Cards List: All Projects -->
                  <v-row v-if="showAllProjects">
                    <v-container fluid>
                      <v-row>
                        <v-col
                          v-for="project in allProjects"
                          :key="`all-row-${project.projectId}-${projectLoadingCounter}`"
                          cols="12"
                          sm="1"
                          md="2"
                          lg="3"
                          xl="3"
                          class="d-flex"
                          min-width="450px"
                        >
                          <ProjectCard
                            :key="`card-${project.projectId}-${projectLoadingCounter}`"
                            :actions="actions"
                            :project="project"
                            :project-status="projectStatusMap[project.projectStatus] || project.projectStatus"
                            :project-type="projectTypeMap[project.projectType] || project.projectType"
                            class="flex-fill"
                            @go-to-project="goToProject"
                            @show-time-entries="showTimeEntries(project)"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-row>
                </v-col>
              </v-row>
            </div>

            <!-- Grouped View -->
            <div v-else>
              <v-row
                v-for="(groupOfProjects, groupName) in groupedProjects"
                :key="groupName"
              >
                <v-col>
                  <!-- Group Header -->
                  <v-row>
                    <v-col>
                      <v-row>
                        <v-col>
                          <!-- Toggle Group -->
                          <font-awesome-icon
                            :icon="['fas', expandedGroups[groupName] ? 'chevron-down' : 'chevron-right']"
                            size="xs"
                            class="gray--text cursor-pointer"
                            @click.stop="toggleGroup(groupName)"
                          />

                          <span
                            class="pl-1 subtitle-1 font-weight-medium cursor-pointer"
                            @click.stop="toggleGroup(groupName)"
                          >{{ groupName }}</span>

                          <!-- Total Projects in Group -->
                          <v-chip
                            class="ml-2"
                            x-small
                          >
                            {{ groupOfProjects.length }}
                          </v-chip>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>

                  <!-- Group Projects -->
                  <v-row v-if="expandedGroups[groupName]">
                    <v-container fluid>
                      <v-row>
                        <v-col
                          v-for="project in groupOfProjects"
                          :key="`group-${groupName}-${project.projectId}`"
                          cols="12"
                          sm="6"
                          md="4"
                          lg="3"
                          xl="2"
                          class="d-flex"
                        >
                          <ProjectCard
                            :key="`grouped-card-${project.projectId}-${projectLoadingCounter}`"
                            :actions="actions"
                            :project="project"
                            :project-status="projectStatusMap[project.projectStatus] || project.projectStatus"
                            :project-type="projectTypeMap[project.projectType] || project.projectType"
                            class="flex-fill"
                            @go-to-project="goToProject"
                            @show-time-entries="showTimeEntries(project)"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-row>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>

        <!-- Table View -->
        <v-row v-else-if="viewMode === 'table'">
          <v-col>
            <!-- Table's Bulk Edit Toolbar -->
            <v-row v-if="selectedProjects.length > 0">
              <v-col>
                <v-toolbar
                  flat
                  color="grey lighten-4"
                  class="mb-3"
                >
                  <v-toolbar-title>{{ selectedProjects.length }} project(s) selected</v-toolbar-title>

                  <v-spacer />

                  <v-btn
                    color="primary"
                    outlined
                    small
                    @click="openBulkEditDialog"
                  >
                    <v-icon
                      left
                      small
                    >
                      edit
                    </v-icon>
                    Bulk Edit
                  </v-btn>
                  <v-btn
                    color="warning"
                    outlined
                    small
                    class="ml-2"
                    @click="bulkArchiveProjects"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'box-archive']"
                      size="sm"
                      class="mr-1"
                    />
                    Archive
                  </v-btn>
                  <v-btn
                    color="error"
                    outlined
                    small
                    class="ml-2"
                    @click="bulkDeleteProjects"
                  >
                    <v-icon
                      left
                      small
                    >
                      delete
                    </v-icon>
                    Delete
                  </v-btn>
                  <v-btn
                    icon
                    small
                    class="ml-2"
                    @click="clearSelection"
                  >
                    <v-icon>close</v-icon>
                  </v-btn>
                </v-toolbar>
              </v-col>
            </v-row>

            <!-- Projects Table -->
            <v-row>
              <v-col>
                <v-data-table
                  v-model="selectedProjects"
                  :headers="tableHeaders"
                  :items="projectResults"
                  :search="searchTerm"
                  :group-by="groupBy"
                  show-select
                  show-group-by
                  item-key="projectId"
                  class="elevation-1"
                  @click:row="goToProject"
                >
                  <template v-slot:group.header="{ group, toggle, isOpen }">
                    <font-awesome-icon
                      :icon="['fas', isOpen ? 'chevron-down' : 'chevron-right']"
                      size="xs"
                      class="gray--text cursor-pointer"
                      @click.stop="toggle"
                    />

                    <span>{{ group }}</span>
                  </template>

                  <!-- Project Name Column -->
                  <template v-slot:item.projectName="{ item }">
                    <div class="font-weight-medium">
                      <span
                        class="cursor-pointer primary--text"
                        @click.stop="goToProject(item)"
                      >
                        {{ item.projectName }}
                      </span>
                    </div>
                  </template>

                  <!-- Status Column -->
                  <template v-slot:item.projectStatus="{ item }">
                    <v-chip
                      :color="item.projectStatusColorBackground"
                      :text-color="item.projectStatusColorText"
                      small
                    >
                      {{ projectStatusMap[item.projectStatus] }}
                    </v-chip>
                  </template>

                  <!-- Progress Column -->
                  <template v-slot:item.progress="{ item }">
                    <div>
                      <v-progress-linear
                        :value="item.projectMinutesConsumedPercentage"
                        :color="item.projectStatusColorBackground"
                        height="8"
                        class="mb-1"
                      />
                      <div class="caption text-center">
                        {{ item.projectMinutesConsumedPercentFormatted }}%
                      </div>
                    </div>
                  </template>

                  <!-- Time Consumed Column -->
                  <template v-slot:item.timeConsumed="{ item }">
                    <div class="text-no-wrap">
                      <div>{{ item.totalBillableTimeFormatted }} / {{ item.projectHoursFormatted }} hrs</div>
                      <div
                        class="caption cursor-pointer primary--text"
                        @click.stop="showTimeEntries(item)"
                      >
                        View Details
                      </div>
                    </div>
                  </template>

                  <!-- Days Remaining Column -->
                  <template v-slot:item.daysRemaining="{ item }">
                    <div class="text-center">
                      <div class="font-weight-medium">
                        {{ item.projectDaysRemaining }}
                      </div>
                      <div class="caption">
                        days
                      </div>
                    </div>
                  </template>

                  <!-- Actions Column -->
                  <template v-slot:item.actions="{ item }">
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          @click.stop
                        >
                          <font-awesome-icon
                            :icon="['fas', 'ellipsis-vertical']"
                            size="sm"
                          />
                        </v-btn>
                      </template>

                      <v-list>
                        <v-list-item
                          v-for="action in actions"
                          :key="action.name"
                          @click="action.event(item)"
                        >
                          <v-list-item-title>{{ action.name }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- No Projects Found -->
    <v-row v-else>
      <v-col>
        <v-card class="px-3 pt-3 pb-6 elevation-0">
          <v-card-text>
            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col class="midgray--text">
                    No projects found. Click "Add Project" to create your first project.
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Show Time Entry View -->
    <v-dialog
      v-model="showTimeEntryView"
      max-width="1200px"
    >
      <v-card>
        <v-card-title class="headline">
          <v-row class="ma-0 align-center">
            <v-col class="col-auto">
              <span>Time Entries</span>

              <div class="body-2">
                <span class="font-weight-medium gray--text">{{ currentProject.projectName }}</span>
              </div>
            </v-col>

            <v-col class="text-right">
              <!-- Download Button -->
              <v-btn
                class="px-3 mx-3"
                color="primary"
                :ripple="false"
                @click.stop="exportTimeEntriesAsCsv(currentProject)"
              >
                Export
              </v-btn>

              <v-btn
                :ripple="false"
                icon
                @click.stop="closeTimeEntryView"
              >
                <v-icon color="midgray">
                  close
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider />

        <v-card-text class="font-weight-medium">
          <v-row>
            <!-- Table of Time Entries -->
            <v-col>
              <v-row>
                <v-col>
                  <v-data-table
                    :headers="timeEntryHeaders"
                    :items="currentTimeEntries"
                    :loading="isLoadingTimeEntries"
                  >
                    <template v-slot:body.append>
                      <!-- Add row for totals -->
                      <tr>
                        <td>
                          <span class="font-weight-bold">Summaries</span>
                          <br>
                        </td>

                        <td colspan="3" />

                        <td>
                          <span>{{ totalMinutesFormatted }} min</span>
                          <br>
                          <span class="vuetifygray--text">{{ totalHoursFormatted }} hrs</span>
                        </td>

                        <td>
                          <span>{{ totalBillableMinutesFormatted }} min</span>
                          <br>
                          <span class="vuetifygray--text">{{ totalBillableHoursFormatted }} hrs</span>
                        </td>
                      </tr>
                    </template>

                    <template v-slot:no-data>
                      No time entries found for this project.
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Bulk Edit Dialog -->
    <v-dialog
      v-model="bulkEditDialog"
      max-width="600px"
    >
      <v-card>
        <v-card-title class="headline">
          Bulk Edit Projects
        </v-card-title>

        <v-card-text>
          <v-form ref="bulkEditForm">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="bulkEditData.projectStatus"
                  :items="projectStatusOptions"
                  label="Project Status"
                  clearable
                  outlined
                  dense
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="bulkEditData.projectType"
                  :items="projectTypeOptions"
                  label="Project Type"
                  clearable
                  outlined
                  dense
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="bulkEditData.startDate"
                      label="Start Date"
                      prepend-icon="event"
                      readonly
                      outlined
                      dense
                      clearable
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="bulkEditData.startDate"
                    @input="startDateMenu = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="6">
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="bulkEditData.endDate"
                      label="End Date"
                      prepend-icon="event"
                      readonly
                      outlined
                      dense
                      clearable
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="bulkEditData.endDate"
                    @input="endDateMenu = false"
                  />
                </v-menu>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-alert
                  type="info"
                  dense
                  text
                >
                  Only filled fields will be applied to the selected {{ selectedProjects.length }} project(s).
                  Empty fields will be ignored.
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="grey darken-1"
            text
            @click="closeBulkEditDialog"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            text
            @click="applyBulkEdit"
          >
            Apply Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import dates from 'src/utils/dates'
import format from 'src/utils/format.js'
import { makeProject } from '@/internal/store/utils/project'
import ProjectCard from './ProjectCard.vue'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapToolsGetters } = createNamespacedHelpers('tools')

export default {
  name: 'ProjectDashboard',
  components: {
    ProjectCard,
  },
  data() {
    return {
      // Tab configuration
      activeTab: 0,
      tabs: [
        {
          id: 'active',
          label: 'Active Projects',
          icon: ['fas', 'play-circle'],
        },
        {
          id: 'all',
          label: 'All Projects',
          icon: ['fas', 'folder-open'],
        },
        {
          id: 'completed',
          label: 'Completed',
          icon: ['fas', 'check-circle'],
        },
        {
          id: 'on-hold',
          label: 'On Hold',
          icon: ['fas', 'pause-circle'],
        },
      ],

      // Data Tools
      searchTerm: '',

      // Filters
      filters: {
        billingCode: null,
        projectStatus: null, // Changed from 'in-progress' since we'll use tabs for status
        projectType: null,
      },
      showFilters: false,

      // View Controls
      viewMode: 'cards',
      groupBy: null,
      selectedProjects: [],
      bulkEditDialog: false,
      bulkEditData: {
        projectStatus: '',
        projectType: '',
        startDate: null,
        endDate: null,
      },

      // Dialog Controls
      startDateMenu: false,
      endDateMenu: false,

      // Flags
      showAllProjects: true,
      testingTimeEntries: false,
      expandedGroups: {},

      // Time Entry View
      currentProject: {},
      currentTimeEntries: [],
      showTimeEntryView: false,
      sortDirectionForTimeEntries: 'DSC',
      totalMinutes: 0,
      totalMinutesFormatted: '0',
      totalHoursFormatted: '0.00',
      totalBillableMinutes: 0,
      totalBillableMinutesFormatted: '0',
      totalBillableHoursFormatted: '0.00',
      totalPersons: 0,

      isLoadingTimeEntries: false,
      projectsById: {},
      projectsWithTimeEntries: [],

      // Reactive incrementor to force view updates
      projectLoadingCounter: 0,

      // Flag to prevent circular dependency when loading time entries
      isLoadingProjectTimeEntries: false,

      processedEditProjectQuery: null,

      timeEntryHeaders: [
        { text: 'Date', value: 'timeEntryDateFormatted', sortable: true },
        { text: 'Person', value: 'personName', sortable: true },
        { text: 'Team', value: 'teamName', sortable: true },
        { text: 'Description', value: 'workNote', sortable: false },
        { text: 'Minutes', value: 'actionDuration', sortable: true },
        { text: 'Billable Minutes', value: 'billableActionDuration', sortable: true },
      ],

      timeSortByKeys: [
        {
          key: 'timeEntryDate',
          direction: 'DSC',
        },
        {
          key: 'personName',
          direction: 'ASC',
        },
      ],

    }
  },
  computed: {
    ...mapAuthGetters([
      'isUserInternalAdmin',
    ]),
    ...mapToolsGetters([
      'projects',
      'projectTimeColumns',
      'projectStatuses',
      'projectTypes',
    ]),

    $projects: {
      get() {
        // Force reactivity when projects finish loading (reference the counter)
        this.projectLoadingCounter // eslint-disable-line no-unused-expressions

        // Ensure projects is always an array before spreading
        const projectsArray = Array.isArray(this.projects) ? this.projects : []

        // Merge this.projects with the projects that have time entries calculated.
        const currentProjects = [...projectsArray].map((project) => {
          const projectWithTimeEntries = this.projectsById[project.projectId]
          return projectWithTimeEntries || project
        })

        const getUndeletedProjects = (projects) => projects.filter((project) => !project.isDeleted)

        return getUndeletedProjects(currentProjects)
      },
      set(value) {
        // Update projects in the store.
        this.$store.commit('tools/projects', value, { root: true })
      },
    },

    haveProjects() {
      return Array.isArray(this.$projects) && this.$projects.length > 0
    },

    // Computed property that ensures cards update when time entries load
    reactiveProjects() {
      // This forces recalculation when projects finish loading
      this.projectLoadingCounter // eslint-disable-line no-unused-expressions
      return this.$projects
    },

    currentTab() {
      return this.tabs[this.activeTab] || this.tabs[0]
    },

    /**
     * Get projects for the current tab based on status
     */
    tabProjects() {
      const { id } = this.currentTab
      const allProjects = this.reactiveProjects || []

      switch (id) {
        case 'active':
          return allProjects.filter((project) => project.projectStatus === 'in-progress'
            || project.projectStatus === 'active'
            || project.projectStatus === 'planning')

        case 'completed':
          return allProjects.filter((project) => project.projectStatus === 'completed'
            || project.projectStatus === 'done')

        case 'on-hold':
          return allProjects.filter((project) => project.projectStatus === 'on-hold'
            || project.projectStatus === 'paused')

        case 'all':
        default:
          return allProjects
      }
    },

    projectResults() {
      let filteredProjects = this.tabProjects || []

      // Apply filters first
      if (this.hasActiveFilters) {
        filteredProjects = filteredProjects.filter((project) => {
          // Filter by billing code
          if (this.filters.billingCode && project.billingCode !== this.filters.billingCode) {
            return false
          }

          // Filter by project status
          if (this.filters.projectStatus && project.projectStatus !== this.filters.projectStatus) {
            return false
          }

          // Filter by project type
          if (this.filters.projectType && project.projectType !== this.filters.projectType) {
            return false
          }

          return true
        })
      }

      // Apply search term filtering
      if (this.searchTerm.length > 0) {
        const searchTerm = this.searchTerm.toLowerCase()

        filteredProjects = filteredProjects.filter((project) => {
          const billingCode = (project.billingCode || '').toLowerCase()
          const projectName = (project.projectName || '').toLowerCase()
          const projectCode = (project.projectCode || '').toLowerCase()
          const projectType = (project.projectType || '').toLowerCase()
          const projectDescription = (project.projectDescription || '').toLowerCase()
          const projectStatus = project.projectStatus.toLowerCase()
          const startDateFormatted = project.startDateFormatted.toLowerCase()
          const endDateFormatted = project.endDateFormatted.toLowerCase()
          const createdDateFormatted = project.createdDateFormatted.toLowerCase()
          const updatedDateFormatted = project.updatedDateFormatted.toLowerCase()

          return (
            projectName.indexOf(searchTerm) > -1
            || billingCode.indexOf(searchTerm) > -1
            || projectCode.indexOf(searchTerm) > -1
            || projectType.indexOf(searchTerm) > -1
            || projectDescription.indexOf(searchTerm) > -1
            || projectStatus.indexOf(searchTerm) > -1
            || startDateFormatted.indexOf(searchTerm) > -1
            || endDateFormatted.indexOf(searchTerm) > -1
            || createdDateFormatted.indexOf(searchTerm) > -1
            || updatedDateFormatted.indexOf(searchTerm) > -1
          )
        })
      }

      return filteredProjects
    },

    allProjects() {
      return this.projectResults
    },

    actions() {
      return [
        {
          event: (project) => this.$store.dispatch('internal/onEditProject', project),
          name: 'Edit',
        },
        {
          event: (project) => this.$store.dispatch('internal/onDeleteProject', project),
          name: 'Delete',
        },
      ]
    },

    tableHeaders() {
      return [
        {
          text: 'Project Name',
          value: 'projectName',
          sortable: true,
        },
        {
          text: 'Code',
          value: 'projectCode',
          sortable: true,
        },
        {
          text: 'Type',
          value: 'projectType',
          sortable: true,
        },
        {
          text: 'Status',
          value: 'projectStatus',
          sortable: true,
        },
        {
          text: 'Progress',
          value: 'progress',
          sortable: false,
        },
        {
          text: 'Time Consumed',
          value: 'timeConsumed',
          sortable: false,
        },
        {
          text: 'Start Date',
          value: 'startDateFormatted',
          sortable: true,
        },
        {
          text: 'End Date',
          value: 'endDateFormatted',
          sortable: true,
        },
        {
          text: 'Days Remaining',
          value: 'daysRemaining',
          sortable: true,
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
        },
      ]
    },

    projectStatusOptions() {
      // Use Firebase project-status collection data if available, otherwise fallback to defaults
      if (this.projectStatuses && this.projectStatuses.length > 0) {
        return this.projectStatuses
      }

      // Fallback to getting the options from the projects array.
      const statuses = [...new Set(
        this.reactiveProjects
          .map((project) => project.projectStatus)
          .filter((status) => status && status.trim() !== ''),
      )].sort()

      return statuses.map((status) => ({
        text: status,
        value: status,
      }))
    },

    projectStatusMap() {
      const map = {}

      this.projectStatuses.forEach((status) => {
        map[status.value] = status.title
      })

      return map
    },

    projectTypeOptions() {
      // Use Firebase project-type collection data if available, otherwise fallback to defaults
      if (this.projectTypes && this.projectTypes.length > 0) {
        return this.projectTypes
      }

      // Fallback to getting the options from the projects array.
      const types = [...new Set(
        this.reactiveProjects
          .map((project) => project.projectType)
          .filter((type) => type && type.trim() !== ''),
      )].sort()

      return types.map((type) => ({
        text: type,
        value: type,
      }))
    },

    projectTypeMap() {
      const map = {}

      this.projectTypes.forEach((type) => {
        map[type.value] = type.title
      })

      return map
    },

    groupByOptions() {
      return [
        { text: 'Status', value: 'projectStatus' },
        { text: 'Billing Code', value: 'projectCode' },
        { text: 'Project Type', value: 'projectType' },
      ]
    },

    billingCodeOptions() {
      // Get unique billing codes from all projects
      const billingCodes = [...new Set(
        this.reactiveProjects
          .map((project) => project.billingCode)
          .filter((code) => code && code.trim() !== ''),
      )].sort()

      return billingCodes.map((code) => ({
        text: code,
        value: code,
      }))
    },

    hasActiveFilters() {
      return !!(this.filters.billingCode || this.filters.projectStatus || this.filters.projectType)
    },

    activeFilterCount() {
      let count = 0
      if (this.filters.billingCode) count += 1
      if (this.filters.projectStatus) count += 1
      if (this.filters.projectType) count += 1
      return count
    },

    isGroupingEnabled() {
      return this.groupBy !== null && this.groupBy !== undefined
    },

    groupedProjects() {
      if (!this.isGroupingEnabled) {
        return {}
      }

      const groups = {}

      this.projectResults.forEach((project) => {
        const groupKey = project[this.groupBy] || 'Unassigned'
        if (!groups[groupKey]) {
          groups[groupKey] = []
        }
        groups[groupKey].push(project)
      })

      // Sort groups alphabetically
      const sortedGroups = {}

      Object.keys(groups).sort().forEach((key) => {
        sortedGroups[key] = groups[key]
      })

      return sortedGroups
    },
  },

  watch: {
    activeTab() {
      // Update tab counts when switching tabs
      this.updateTabCounts()
    },

    reactiveProjects: {
      deep: true,
      handler() {
        this.updateTabCounts()
      },
    },

    groupBy() {
      this.initializeExpandedGroups()
    },
    currentProject: {
      deep: true,
      immediate: true,
      handler() {
        // if (newProject && newProject.projectId) {
        //   this.currentTimeEntries = this.currentTimeEntries[newProject.projectId] || []
        // } else {
        //   this.currentTimeEntries = []
        // }
      },
    },
    // Watch only the store projects, not the computed $projects that includes projectsById
    projects: {
      deep: true,
      async handler(newProjects, oldProjects) {
        // Prevent circular dependency when we're already loading time entries
        if (this.isLoadingProjectTimeEntries) {
          return false
        }

        const newProjectsJson = JSON.stringify(newProjects)
        const oldProjectsJson = JSON.stringify(oldProjects)

        if (newProjectsJson === oldProjectsJson) {
          return false // No changes detected
        }

        // Only process if projects are loaded.
        if (newProjects && newProjects.length > 0) {
          this.setView(this.query)
        }

        return true
      },
    },
    allProjects: {
      deep: true,
      immediate: true,
      handler() {
        // Do nothing. This is just to trigger reactivity.
      },
    },
    groupedProjects: {
      deep: true,
      immediate: true,
      handler() {
        // Do nothing. This is just to trigger reactivity.
      },
    },
  },

  async created() {
    // Fetch project statuses and types from Firebase when component is created
    try {
      await Promise.all([
        this.$store.dispatch('tools/fetchProjectStatuses'),
        this.$store.dispatch('tools/fetchProjectTypes'),
      ])
    } catch (error) {
      // Silently fail - component will use fallback options
    }

    // Initialize tab counts
    this.updateTabCounts()
  },

  methods: {
    /**
     * Update tab counts based on current projects
     */
    updateTabCounts() {
      // Safety check to ensure tabs exist
      if (!this.tabs || !Array.isArray(this.tabs)) {
        return
      }

      const allProjects = this.reactiveProjects || []

      this.tabs = this.tabs.map((tab) => {
        let count = 0

        switch (tab.id) {
          case 'active':
            count = allProjects.filter((project) => project.projectStatus === 'in-progress'
              || project.projectStatus === 'active'
              || project.projectStatus === 'planning').length
            break

          case 'all':
            count = allProjects.length
            break

          case 'completed':
            count = allProjects.filter((project) => project.projectStatus === 'completed'
              || project.projectStatus === 'done').length
            break

          case 'on-hold':
            count = allProjects.filter((project) => project.projectStatus === 'on-hold'
              || project.projectStatus === 'paused').length
            break

          default:
            count = 0
        }

        return { ...tab, count }
      })
    },

    /**
     * Show create project form
     */
    showFormProject() {
      const action = 'create'
      const formData = makeProject({})

      this.$store.dispatch('internal/showFormProject', { action, formData })
    },

    closeTimeEntryView() {
      this.showTimeEntryView = false
    },

    goToProject(project) {
      this.$store.dispatch('internal/goToProject', project)
    },

    sortTimeEntriesByDateAsc(a, b) {
      return new Date(a.timeEntryDate) - new Date(b.timeEntryDate)
    },
    sortTimeEntriesByDateDsc(a, b) {
      return new Date(b.timeEntryDate) - new Date(a.timeEntryDate)
    },
    sortTimeEntriesByAsc(key) {
      if (key === 'timeEntryDate') {
        return this.sortTimeEntriesByDateAsc
      }

      return (a, b) => {
        if (a[key] < b[key]) {
          return -1
        }

        if (a[key] > b[key]) {
          return 1
        }

        return 0
      }
    },
    sortTimeEntriesByDsc(key) {
      if (key === 'timeEntryDate') {
        return this.sortTimeEntriesByDateDsc
      }

      return (a, b) => {
        if (a[key] > b[key]) {
          return -1
        }

        if (a[key] < b[key]) {
          return 1
        }

        return 0
      }
    },

    sortTimeEntries({ direction, key }) {
      return (a, b) => {
        if (direction === 'ASC') {
          return this.sortTimeEntriesByAsc(key)(a, b)
        }

        return this.sortTimeEntriesByDsc(key)(a, b)
      }
    },

    toggleAllProjects() {
      this.showAllProjects = !this.showAllProjects
    },

    toggleGroup(groupName) {
      this.$set(this.expandedGroups, groupName, !this.expandedGroups[groupName])
    },

    initializeExpandedGroups() {
      // Initialize all groups as expanded when grouping changes
      if (this.isGroupingEnabled) {
        const newExpandedGroups = {}
        Object.keys(this.groupedProjects).forEach((groupName) => {
          newExpandedGroups[groupName] = true
        })
        this.expandedGroups = newExpandedGroups
      }
    },

    clearAllFilters() {
      this.filters = {
        billingCode: null,
        projectStatus: null,
        projectType: null,
      }
    },

    /**
     * @method showTimeEntries
     * @desc Set the view's data before revealing the view to the user.
     * @param {Object} project
     * @param {Promise} project.timeEntries
     * @return {void}
     */
    async showTimeEntries(project) {
      // Set the selected project on the view.
      this.currentProject = project

      // Indicate loading state on the project.
      project.isLoadingTimeEntries = true

      // Indicate loading state on the view.
      this.isLoadingTimeEntries = true

      // Reveal the view.
      this.showTimeEntryView = true

      // Get the project's time entries.
      const projectTimeEntries = project.timeEntries || []

      /**
       * Sort the view's time entries by date.
       * Set the sorted time entries on the view.
       *
       * Apply the sorting configurations in reverse order
       * to ensure the user's latest sort order overrides
       * all previous sort orders.
       */

      // eslint-disable-next-line arrow-body-style
      this.currentTimeEntries = this.timeSortByKeys.reduceRight((sortedArray, sortingConfig) => {
        /**
         * @const {function} sortByKeyAndDirection
         * @desc A function configured with a key and direction to sort by.
         */
        const sortByKeyAndDirection = this.sortTimeEntries(sortingConfig)

        // Return the sorted array.
        return sortedArray.sort(sortByKeyAndDirection)
      }, projectTimeEntries)

      const currentTimeEntriesForProject = this.currentTimeEntries || []

      // Set the view's calculations.

      // Calculate total minutes.
      this.totalMinutes = currentTimeEntriesForProject.reduce((sum, entry) => sum + entry.actionDuration, 0)

      // Format total minutes.
      this.totalMinutesFormatted = format.number(this.totalMinutes)

      this.totalHoursFormatted = format.hhmm(this.totalMinutes)

      // Calculate total billable minutes.
      this.totalBillableMinutes = currentTimeEntriesForProject.reduce((sum, entry) => sum + entry.billableActionDuration, 0)

      // Format total billable minutes.
      this.totalBillableMinutesFormatted = format.number(this.totalBillableMinutes)

      this.totalBillableHoursFormatted = format.hhmm(this.totalBillableMinutes)

      // Calculate total persons who have logged time.
      this.totalPersons = [...new Set(currentTimeEntriesForProject.map((entry) => entry.personName))].length

      // Remove the loading state on the project.
      project.isLoadingTimeEntries = false

      // Remove the loading state on the view.
      this.isLoadingTimeEntries = false
    },

    /**
     * @method hideTimeEntries
     * @desc Hide the view from the user before resetting the view's data.
     * @return {void}
     */
    hideTimeEntries() {
      // Hide the view.
      this.showTimeEntryView = false

      // Reset the view's data.
      this.currentProject = {}
      this.currentTimeEntries = []

      // Reset the view's calculations.
      this.totalMinutes = 0
      this.totalMinutesFormatted = '0'
      this.totalHoursFormatted = '0.00'

      this.totalBillableMinutes = 0
      this.totalBillableMinutesFormatted = '0'
      this.totalBillableHoursFormatted = '0.00'

      this.totalPersons = 0

      // Reset the view's sort direction.
      this.sortDirectionForTimeEntries = 'DSC'

      // Reset the loading state.
      this.isLoadingTimeEntries = false
    },

    exportTimeEntriesAsCsv(project) {
      const {
        currentTimeEntries,
        projectTimeColumns: exportColumns,
        timeSortByKeys: sortByKeys,
      } = this

      const { projectCode } = project

      const todayFormatted = dates.getFormattedDate(undefined, 'YYYY-MM-DD')

      const exportFormat = 'csv'
      const exportName = 'time-entries'
      const exportFileName = `Time Entries for ${projectCode} (Extracted ${todayFormatted})`

      const exportGroups = [
        {
          items: currentTimeEntries,
        },
      ]

      const configExport = {
        exportColumns,
        exportFileName,
        exportFormat,
        exportGroups,
        exportName,
        sortByKeys,
      }

      return this.$store.dispatch('tools/exportAs', configExport)
    },

    // Bulk Operations
    clearSelection() {
      this.selectedProjects = []
    },

    openBulkEditDialog() {
      this.bulkEditDialog = true
    },

    async bulkArchiveProjects() {
      if (this.selectedProjects.length === 0) return

      try {
        const updatePromises = this.selectedProjects.map((project) => this.$store.dispatch('internal/updateProject', {
          ...project,
          isArchived: true,
        }))

        await Promise.all(updatePromises)

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `${this.selectedProjects.length} projects archived successfully`,
          status: 'success',
        }, { root: true })

        this.clearSelection()
      } catch (error) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Error archiving projects',
          status: 'error',
        }, { root: true })
      }
    },

    closeBulkEditDialog() {
      this.bulkEditDialog = false
      this.bulkEditData = {
        projectStatus: '',
        projectType: '',
        startDate: null,
        endDate: null,
      }
    },

    async applyBulkEdit() {
      if (this.selectedProjects.length === 0) return

      try {
        const updatePromises = this.selectedProjects.map((project) => {
          const updates = { ...project }

          // Only apply non-empty values
          if (this.bulkEditData.projectStatus) {
            updates.projectStatus = this.bulkEditData.projectStatus
          }
          if (this.bulkEditData.projectType) {
            updates.projectType = this.bulkEditData.projectType
          }
          if (this.bulkEditData.startDate) {
            updates.startDate = this.bulkEditData.startDate
          }
          if (this.bulkEditData.endDate) {
            updates.endDate = this.bulkEditData.endDate
          }

          return this.$store.dispatch('internal/updateProject', updates)
        })

        await Promise.all(updatePromises)

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `${this.selectedProjects.length} projects updated successfully`,
          status: 'success',
        }, { root: true })

        this.closeBulkEditDialog()
        this.clearSelection()
      } catch (error) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Error updating projects',
          status: 'error',
        }, { root: true })
      }
    },

    async bulkDeleteProjects() {
      if (this.selectedProjects.length === 0) return

      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(`Are you sure you want to delete ${this.selectedProjects.length} projects?`)
      if (!confirmed) return

      try {
        const deletePromises = this.selectedProjects.map((project) => this.$store.dispatch('internal/deleteProject', project))

        await Promise.all(deletePromises)

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `${this.selectedProjects.length} projects deleted successfully`,
          status: 'success',
        }, { root: true })

        this.clearSelection()
      } catch (error) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Error deleting projects',
          status: 'error',
        }, { root: true })
      }
    },

    findProjectByIdOrCode(identifier) {
      if (!this.projectResults || !Array.isArray(this.projectResults)) {
        return null
      }

      // Try to find by project ID first, then by project code
      return this.projectResults.find((project) => project.projectId === identifier
    || project.project_id === identifier
    || project.projectCode === identifier
    || project.project_code === identifier)
    },

    handleEditProjectQuery(query = {}) {
      const { editProject } = query

      // Don't process if no editProject parameter, no access, modal already visible,
      // or we've already processed this exact query
      if (!editProject
      || !this.hasAccessToProjectManagement
      || this.isProjectModalVisible
      || this.processedEditProjectQuery === editProject) {
        // Return false to indicate we did not process the query.
        return false
      }

      // Find the project by ID or code
      const project = this.findProjectByIdOrCode(editProject)

      if (project) {
        // Mark this query as processed to prevent duplicate processing
        this.processedEditProjectQuery = editProject

        // Use the existing onEditProject action to open the modal
        this.$store.dispatch('internal/onEditProject', project)

        // Clear the query parameter to prevent reopening the modal on navigation
        this.$router.replace({
          path: this.$route.path,
          params: this.$route.params,
          query: { ...this.$route.query, editProject: undefined },
        })
      }

      // Indicate that we processed the query (even if no project found).
      return true
    },

    async setTimeEntriesOnProject(project) {
      // Fetch and store the time entries for all projects.
      const timeEntriesResponseFromApi = await this.$store.dispatch('tools/fetchTimeEntriesByProjectId', project.projectId)

      // Handle error response from API.
      if (!timeEntriesResponseFromApi) {
        const message = 'No response received when fetching time entries for projects.'

        // Show a message to the user.
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message,
          status: 'error',
        }, { root: true })

        // Exit early with empty time entries.
        return timeEntriesResponseFromApi || { error: true, message }
      }

      // Handle error response from API.
      if (timeEntriesResponseFromApi.error) {
        const message = timeEntriesResponseFromApi.message
          || timeEntriesResponseFromApi.error?.message
          || 'Unknown error occurred when fetching time entries for projects.'

        // Show a message to the user.
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message,
          status: 'error',
        }, { root: true })

        // @todo Inform the user of the error.
        // Show a notification or message to the user.

        // Exit early with empty time entries.
        return timeEntriesResponseFromApi || { error: true, message }
      }

      const timeEntriesFromApi = timeEntriesResponseFromApi.content || []

      // Ensure timeEntriesFromApi is an array before mapping
      if (!Array.isArray(timeEntriesFromApi)) {
        const message = 'Invalid time entries data received from API.'

        // Show a message to the user.
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message,
          status: 'error',
        }, { root: true })

        return { error: true, message }
      }

      // Transform each time entry, creating an array of promises.
      // eslint-disable-next-line arrow-body-style
      const timeEntryPromises = timeEntriesFromApi.map((timeEntry) => {
        return this.$store.dispatch('tools/transformTimeItem', timeEntry)
      })

      // Wait for all transformations to complete, resulting in the
      // final time entries array.
      const timeEntries = await Promise.all(timeEntryPromises)

      // Update projects with its time entries and calculations.
      const projectWithTimeEntries = await this.$store.dispatch('tools/calculateProjectHours', {
        timeEntries,
        project,
      })

      // Update the project's loading state.
      projectWithTimeEntries.isLoadingTimeEntries = false

      // Store the project with time entries in a map for quick access.
      this.$set(this.projectsById, project.projectId, projectWithTimeEntries)

      // Increment counter to force reactive updates
      this.projectLoadingCounter += 1

      return projectWithTimeEntries
    },

    async setProjectsWithTimeEntries(projects) {
      // Load time entries for each project individually (no waiting)
      // This allows cards to update as each one finishes loading
      projects.forEach((project) => {
        // Set loading state immediately for this project
        project.isLoadingTimeEntries = true
        this.setTimeEntriesOnProject(project)
      })
    },

    async setView(query) {
      // Handle the editProject query parameter if present.
      const isEditing = this.handleEditProjectQuery(query)

      // Fetch and store the time entries for all projects if not editing.
      if (!isEditing) {
        const projects = this.projects || []

        // Set flag to prevent circular dependency
        this.isLoadingProjectTimeEntries = true

        try {
          this.setProjectsWithTimeEntries(projects)
        } finally {
          // Clear flag after a short delay to allow all async operations to complete
          setTimeout(() => {
            this.isLoadingProjectTimeEntries = false
          }, 100)
        }
      }

      return true
    },
  },
}
</script>

<style lang="scss" scoped>
// Header styles (matching Time and Organization views)
.projects-view-header-compact {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden; // Ensures children respect border-radius

  .header-toolbar {
    min-height: 56px !important;
    max-height: 56px !important;
    background: white !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  .header-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(var(--v-primary-base), 0.08);
  }

  .projects-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      height: 56px !important;
      background-color: white !important;
    }

    ::v-deep .v-tab {
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.25px;
      text-transform: none;
      min-width: 120px;
      padding: 0 16px;
      height: 56px;

      &:hover {
        background-color: rgba(var(--v-primary-base), 0.04);
      }
    }

    ::v-deep .v-tab--active {
      color: var(--v-primary-base);
    }

    ::v-deep .v-tabs-slider {
      height: 3px !important;
    }
  }

  .tab-compact {
    padding: 0 16px;
  }

  .add-project-btn-compact {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
  }

  // Tab count chips styling
  .tab-count-chip {
    background-color: #e0e0e0 !important;
    border-color: #bdbdbd !important;
    color: #616161 !important;
    font-weight: 600;
  }
}

// Controls bar styles (matching Time and Organization views)
.controls-bar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);

  .search-field-compact {
    ::v-deep .v-input__control {
      min-height: 40px;
    }

    ::v-deep .v-input__slot {
      background-color: white !important;
    }

    .search-icon {
      color: rgba(0, 0, 0, 0.54);
    }
  }

  .filter-btn-compact {
    text-transform: none;
    letter-spacing: normal;
    height: 40px;

    &.filter-active {
      background-color: rgba(var(--v-primary-base), 0.08) !important;
      border-color: var(--v-primary-base) !important;
      border-width: 2px !important;
    }
  }

  .group-select-compact {
    ::v-deep .v-input__control {
      min-height: 40px;
    }
  }
}

// Dark mode support
.theme--dark {
  .projects-view-header-compact {
    background: #2d2d2d;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);

    .header-toolbar {
      background: #2d2d2d !important;
      border-bottom-color: rgba(255, 255, 255, 0.12);
    }

    .header-icon-wrapper {
      background: rgba(var(--v-primary-base), 0.15);
    }

    .projects-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        background-color: #2d2d2d !important;
      }
    }

    .tab-count-chip {
      background-color: #424242 !important;
      border-color: #616161 !important;
      color: #e0e0e0 !important;
    }
  }

  .controls-bar {
    background: rgba(255, 255, 255, 0.05);
    border-bottom-color: rgba(255, 255, 255, 0.12);

    .search-field-compact {
      ::v-deep .v-input__slot {
        background-color: rgba(255, 255, 255, 0.05) !important;
      }

      .search-icon {
        color: rgba(255, 255, 255, 0.7);
      }
    }

    .filter-btn-compact.filter-active {
      background-color: rgba(var(--v-primary-base), 0.15) !important;
    }
  }
}
</style>
